/**
 * InsiStatusBadge (T10) — the INSi identity-reliability pill.
 *
 * Renders `identityReliabilityStatus` (Provisoire / Récupérée / Qualifiée) as a
 * coloured pill with an explanatory tooltip. Colour is driven by STATUS only
 * (design): orange = Provisoire, blue = Récupérée, green = Qualifiée. It is
 * INDEPENDENT of the INS type (NIR/NIA), which is shown as plain text elsewhere.
 *
 * Shared: reused on the patient-record INS card and on the patients-list row.
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';

import { INSI_STATUS_COLORS } from './insiStatusColors';
import { INSI_STATUS_LABEL as STATUS_LABEL } from './insiStatus';

/** Status → icon (used in both the pill and the compact list badge). */
const STATUS_ICON = {
  PROVISOIRE: ErrorOutlineIcon,
  RECUPEREE: CheckCircleIcon,
  QUALIFIEE: CheckCircleIcon,
};

/** Status → tooltip copy explaining what the status means. */
const STATUS_TOOLTIP = {
  PROVISOIRE: 'Provisional identity — not yet verified against INSi.',
  RECUPEREE: 'Identity retrieved from INSi; an INS is assigned.',
  QUALIFIEE: 'Identity verified with an attested identity document.',
};

export default function InsiStatusBadge({ status, className, compact }) {
  const { t } = useTranslation();

  if (!status || !STATUS_LABEL[status]) return null;

  const { accent, bg } = INSI_STATUS_COLORS[status];
  const Icon = STATUS_ICON[status];

  // Compact (patients-list row): just the status-coloured 16px icon sitting
  // inline next to the name; the status wording shows on hover.
  if (compact) {
    return (
      <Tooltip title={t(STATUS_LABEL[status])} arrow>
        <SIconWrap className={className} data-id={`insi-status-${status}`}>
          <Icon htmlColor={accent} sx={{ fontSize: 16 }} />
        </SIconWrap>
      </Tooltip>
    );
  }

  // The retrieved/qualified states carry a check icon; Provisoire does not.
  const withIcon = status === 'QUALIFIEE' || status === 'RECUPEREE';

  return (
    <Tooltip title={t(STATUS_TOOLTIP[status])} arrow>
      <SPill
        className={className}
        data-id={`insi-status-${status}`}
        $bg={bg}
        $color={accent}
      >
        {withIcon && (
          <CheckCircleIcon htmlColor={accent} sx={{ fontSize: 16 }} />
        )}
        {t(STATUS_LABEL[status])}
      </SPill>
    </Tooltip>
  );
}

InsiStatusBadge.propTypes = {
  status: PropTypes.oneOf(['PROVISOIRE', 'RECUPEREE', 'QUALIFIEE']),
  className: PropTypes.string,
  /** Icon-only variant for dense rows (patients list). */
  compact: PropTypes.bool,
};

InsiStatusBadge.defaultProps = {
  status: undefined,
  className: undefined,
  compact: false,
};

const SIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  line-height: 0;
`;

const SPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 500;
  line-height: 19px;
  white-space: nowrap;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;
